import type { JourneyJson } from '../types/JourneyJson';
import type { LegJson } from '../types/LegJson';

/** Narrowed walking-leg variant used by every summary helper below. */
export type WalkingLeg = Extract<LegJson, { type: 'walking' }>;

/** Coord entered by the user, parsed from a "lat,lon" string. */
export type Coord = readonly [number, number];

/** Pre-computed metadata the walk viewer's sidebar renders. */
export interface WalkSummary {
  /** Total walking seconds summed across every walking leg. */
  totalSeconds: number;
  /** Total walking metres summed across every walking leg. */
  totalMeters: number;
  /** Per-leg summary entries, one for each walking leg. */
  legs: readonly WalkLegSummary[];
}

/** One row in the walk viewer's per-leg table. */
export interface WalkLegSummary {
  /** 1-based index for stable rendering keys. */
  index: number;
  /** Walking seconds for this leg. */
  seconds: number;
  /** Walking metres for this leg. */
  meters: number;
  /** Number of geometry vertices Dijkstra produced for this leg. */
  vertexCount: number;
  /** Entrance coord as "lat, lon" when the API attached one, else empty. */
  entranceLabel: string;
}

/** True when `text` parses as `"<lat>,<lon>"` with both values finite. */
export function parseCoord(text: string): Coord | undefined {
  const parts = text.split(',');
  if (parts.length !== 2) return undefined;
  const [latText, lonText] = parts;
  return numericPairFromParts([latText, lonText]);
}

/** Splits the two-piece split output into a numeric pair, or undefined. Caller
 * has already verified `parts.length === 2` so the tuple destructure is total. */
function numericPairFromParts(parts: readonly [string, string]): Coord | undefined {
  const [latText, lonText] = parts;
  const latitude = Number(latText.trim());
  const longitude = Number(lonText.trim());
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return undefined;
  return [latitude, longitude];
}

/** Returns only the walking legs from `journey`, in order. Empty when none. */
export function walkingLegs(journey: JourneyJson | undefined): readonly WalkingLeg[] {
  if (!journey) return [];
  return journey.legs.filter((legItem) => isWalkingLeg(legItem));
}

/** Type predicate for the walking variant of `LegJson`. */
function isWalkingLeg(legItem: LegJson): legItem is WalkingLeg {
  return legItem.type === 'walking';
}

/** Formats a wire entrance coord `[lat, lon]` as `"lat, lon"` with 5 decimals. */
function formatEntrance(entrance: readonly [number, number] | undefined): string {
  if (!entrance) return '';
  return `${entrance[0].toFixed(5)}, ${entrance[1].toFixed(5)}`;
}

/** Builds a per-leg summary row for one walking leg. */
function summariseLeg(walk: WalkingLeg, position: number): WalkLegSummary {
  return {
    index: position + 1,
    seconds: walk.walk_seconds,
    meters: walk.walk_meters,
    vertexCount: walk.geometry.length,
    entranceLabel: formatEntrance(walk.via_entrance),
  };
}

/** Sums `walk_seconds` across every walking leg. Extracted to keep {@link walkSummary} small. */
function totalSecondsOf(walks: readonly WalkingLeg[]): number {
  let total = 0;
  for (const walk of walks) total += walk.walk_seconds;
  return total;
}

/** Sums `walk_meters` across every walking leg. Extracted to keep {@link walkSummary} small. */
function totalMetersOf(walks: readonly WalkingLeg[]): number {
  let total = 0;
  for (const walk of walks) total += walk.walk_meters;
  return total;
}

/** Maps each walking leg to its sidebar-row summary. */
function summaryRows(walks: readonly WalkingLeg[]): WalkLegSummary[] {
  return walks.map((walk, position) => summariseLeg(walk, position));
}

/**
 * Derives the walk viewer's sidebar summary from `journey`. Returns a zero-filled
 * summary for an empty leg list or an absent journey.
 */
export function walkSummary(journey: JourneyJson | undefined): WalkSummary {
  const walks = walkingLegs(journey);
  return {
    totalSeconds: totalSecondsOf(walks),
    totalMeters: totalMetersOf(walks),
    legs: summaryRows(walks),
  };
}

/**
 * Encodes `origin` and `destination` as the query object `getRoutes` expects.
 * Callers feed this straight into `apiGet('/v1/routes', ...)` for the coord case.
 */
export function coordQuery(origin: Coord, destination: Coord): Record<string, number> {
  return {
    origin_lat: origin[0],
    origin_lon: origin[1],
    dest_lat: destination[0],
    dest_lon: destination[1],
  };
}
